import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { Review, ReviewSummary } from '../types/review';

// Re-export types for convenience
export type { Review, ReviewSummary };
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Gets reviews for a specific user (professional)
 */
export const getUserReviews = async (userId: string, limitCount: number = 10): Promise<Review[]> => {
  try {
    const reviewsRef = collection(db, 'users', userId, 'reviews');
    const q = query(
      reviewsRef,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const reviews: Review[] = [];

    querySnapshot.forEach((docSnapshot) => {
      const reviewData = docSnapshot.data();
      // Only include non-hidden reviews
      if (reviewData.isHidden !== true) {
        reviews.push({
          id: docSnapshot.id,
          ...reviewData
        } as Review);
      }
    });

    return reviews;
  } catch (error) {
    console.error('Error getting user reviews:', error);
    return [];
  }
};

/**
 * Creates a new review for a user using Cloud Function
 */
export const createReview = async (revieweeId: string, reviewData: Omit<Review, 'id'>): Promise<string | null> => {
  try {
    const functions = getFunctions();
    const createReviewFunction = httpsCallable(functions, 'createReview');

    const result = await createReviewFunction({
      revieweeId,
      rating: reviewData.rating,
      comment: reviewData.comment,
      bookingId: reviewData.bookingId,
      serviceCategory: reviewData.serviceCategory,
      meetingDate: reviewData.meetingDate
    });

    const data = result.data as { success: boolean; reviewId: string };
    if (data.success) {
      return data.reviewId;
    } else {
      console.error('Cloud Function returned error');
      return null;
    }
  } catch (error) {
    console.error('Error creating review via Cloud Function:', error);
    return null;
  }
};

/**
 * Gets review summary statistics for a user
 */
export const getUserReviewSummary = async (userId: string): Promise<ReviewSummary | null> => {
  try {
    const reviewsRef = collection(db, 'users', userId, 'reviews');
    const q = query(reviewsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const reviews: Review[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const reviewData = docSnapshot.data();
      // Only include non-hidden reviews in summary
      if (reviewData.isHidden !== true) {
        reviews.push({
          id: docSnapshot.id,
          ...reviewData
        } as Review);
      }
    });

    if (reviews.length === 0) {
      return {
        userId,
        totalReviews: 0,
        averageRating: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        lastUpdated: Timestamp.now()
      };
    }

    // Calculate statistics
    const totalReviews = reviews.length;
    const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = totalRating / totalReviews;

    // Calculate rating distribution
    const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach(review => {
      ratingDistribution[review.rating as keyof typeof ratingDistribution]++;
    });

    return {
      userId,
      totalReviews,
      averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal
      ratingDistribution,
      lastUpdated: Timestamp.now()
    };
  } catch (error) {
    console.error('Error getting user review summary:', error);
    return null;
  }
};

/**
 * Formats a rating as stars (for display)
 */
export const formatRatingStars = (rating: number): string => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return '★'.repeat(fullStars) +
         (hasHalfStar ? '☆' : '') +
         '☆'.repeat(emptyStars);
};

/**
 * Formats time ago (e.g., "2 weeks ago")
 */
export const formatTimeAgo = (timestamp: Timestamp): string => {
  const now = new Date();
  const reviewDate = timestamp.toDate();
  const diffMs = now.getTime() - reviewDate.getTime();

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffYears > 0) return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  if (diffMonths > 0) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  if (diffWeeks > 0) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return 'Today';
};